import React from 'react'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import { defaltMapPayload } from 'common/config'
import { MapStyles } from 'common/consts'
import { Coordinates, MapStyle } from 'common/types'
import mapboxgl from 'mapbox-gl'

type MapContextType = {
  mapbox: mapboxgl.Map | null
  setMapbox: React.Dispatch <React.SetStateAction<mapboxgl.Map | null>>

  language: MapboxLanguage

  lngLat: mapboxgl.LngLat
  setLatLng: React.Dispatch <React.SetStateAction<mapboxgl.LngLat>>

  zoom: number
  setZoom: React.Dispatch <React.SetStateAction<number>>

  intZoom: number
  setIntZoom: React.Dispatch <React.SetStateAction<number>>

  style: MapStyle
  setStyle: React.Dispatch <React.SetStateAction<MapStyle>>

  hasSource: (sourceId: string) => boolean

  hasLayer: (layerId: string) => boolean

  addSource: (sourceId: string, source: mapboxgl.AnySourceData) => void

  addLayer: (layer: mapboxgl.AnyLayer) => void

  removeSource: (sourceId: string) => void

  removeLayer: (layerId: string) => void

  loadImage: (imageId: string, url: string, sdf?: boolean) => void

  addSvg: (imageId: string, size: number, src: string) => void

  updateSourceData: (sourceId: string, data: any) => void

  updateImageSource: (sourceId: string, url: string, coordinates?: Coordinates) => void

  setView: (lng: number, lat: number, zoom?: number) => void
}

export const MapContext = React.createContext<MapContextType | undefined>(undefined)

export const MapProvider = ({children}: React.PropsWithChildren) => {
  const [mapbox, setMapbox] = React.useState<mapboxgl.Map | null>(null)
  const [language] = React.useState<MapboxLanguage>(new MapboxLanguage())
  const [lngLat, setLatLng] = React.useState<mapboxgl.LngLat>(defaltMapPayload.center)
  const [zoom, setZoom] = React.useState<number>(defaltMapPayload.zoom)
  const [intZoom, setIntZoom] = React.useState<number>(Math.floor(defaltMapPayload.zoom))
  const [style, setStyle] = React.useState<MapStyle>(MapStyles[0])

  const hasSource = (sourceId: string): boolean => {
    if (!mapbox) return false
    if (mapbox.getSource(sourceId)) {
      return true
    } else {
      return false
    }
  }

  const hasLayer = (layerId: string): boolean => {
    if (!mapbox) return false
    if (mapbox.getLayer(layerId)) {
      return true
    } else {
      return false
    }
  }

  const addSource = (sourceId: string, source: mapboxgl.AnySourceData) => {
    if (!mapbox) return
    if (mapbox.getSource(sourceId)) return
    mapbox.addSource(sourceId, source)
  }

  const addLayer = (layer: mapboxgl.AnyLayer) => {
    if (!mapbox) return
    if (mapbox.getLayer(layer.id)) return
    // console.log('[add]', layer.id)
    mapbox.addLayer(layer)
  }

  const removeSource = (sourceId: string) => {
    if (!mapbox) return
    if (mapbox.getSource(sourceId)) {
      mapbox.removeSource(sourceId)
    }
  }

  const removeLayer = (layerId: string) => {
    if (!mapbox) return
    if (mapbox.getLayer(layerId)) {
      // console.log('[remove]', layerId)
      mapbox.removeLayer(layerId)
    }
  }

  const loadImage = (imageId: string, url: string, sdf = true) => {
    if (!mapbox) return
    if (mapbox.hasImage(imageId)) {
      return
    }
    mapbox.loadImage(url, (error, image) => {
      if (error) throw error
      if (!mapbox || !image) return
      mapbox.addImage(imageId, image, { sdf: sdf })
    })
  }

  const addSvg = (imageId: string, size: number, svgData: string) => {
    if (!mapbox) return
    if (mapbox.hasImage(imageId)) {
      return
    }
    const img = new Image(size, size)
    img.onload = () => mapbox.addImage(imageId, img, { sdf: false })
    img.src = 'data:image/svg+xml,' + encodeURIComponent(svgData)
  }

  const updateSourceData = (sourceId: string, data: any) => {
    if (!mapbox) return
    const source: any = mapbox.getSource(sourceId)
    if (source) {
      source.setData(data)
    }
  }

  const updateImageSource = (sourceId: string, url: string, coordinates?: Coordinates) => {
    if (!mapbox) return
    const source: any = mapbox.getSource(sourceId)
    if (source) {
      source.updateImage({url: url})
      if (coordinates !== undefined) {
        source.setCoordinates(coordinates)
      }
    }
  }

  const setView = (lng: number, lat: number, zoom?: number) => {
    if (!mapbox) return
    mapbox.setCenter(new mapboxgl.LngLat(lng, lat))
    if (zoom) {
      mapbox.setZoom(zoom)
    }
  }

  const value = React.useMemo(() => ({
    mapbox, setMapbox,
    lngLat, setLatLng,
    zoom, setZoom,
    intZoom, setIntZoom,
    style, setStyle,
    hasSource,
    hasLayer,
    addSource,
    addLayer,
    removeSource,
    removeLayer,
    loadImage,
    addSvg,
    updateSourceData,
    updateImageSource,
    setView,
    language,
  }), [
    mapbox, setMapbox,
    lngLat, setLatLng,
    zoom, setZoom,
    intZoom, setIntZoom,
    style, setStyle,
    hasSource,
    hasLayer,
    addSource,
    addLayer,
    removeSource,
    removeLayer,
    loadImage,
    addSvg,
    updateSourceData,
    updateImageSource,
    setView,
    language,
  ])

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  )
}
